/**
 * Tax Report — /finance/reports/tax
 * Output / input tax reconciliation with SG GST / MY SST / Generic modes.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Receipt, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

// ── Types ─────────────────────────────────────────────────────────────────────

interface TaxLine {
  code: string;
  label: string;
  count: number;
  grossAmount: number;
  taxableAmount: number;
  taxAmount: number;
}

interface GstBoxes {
  box1: number;
  box5: number;
  box6: number;
  box7: number;
  box9: number;
}

interface TaxData {
  reportType: string;
  period: { from: string; to: string };
  jurisdiction: string;
  taxRate: number;
  summary: {
    totalOutputTax: number;
    totalInputTax: number;
    netTaxPayable: number;
    isRefund: boolean;
  };
  outputLines: TaxLine[];
  inputLines: TaxLine[];
  gstBoxes: GstBoxes | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const JURISDICTIONS = [
  { id: 'sg', label: 'Singapore GST (9%)' },
  { id: 'my', label: 'Malaysia SST (8%)' },
  { id: 'generic', label: 'Generic (7%)' },
];

function fmt(n: number) {
  return new Intl.NumberFormat('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function thisYear(): { from: string; to: string } {
  const now = new Date();
  return { from: `${now.getFullYear()}-01-01`, to: now.toISOString().split('T')[0]! };
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function TaxReportPage() {
  const [data, setData]             = useState<TaxData | null>(null);
  const [loading, setLoading]       = useState(true);
  const [jurisdiction, setJurisdiction] = useState('sg');
  const [from, setFrom]             = useState(thisYear().from);
  const [to, setTo]                 = useState(thisYear().to);
  const loadedRef = useRef(false);

  const fetchData = useCallback(async (f: string, t: string, j: string) => {
    setLoading(true);
    try {
      const res = await api.get<TaxData>(`/finance/reports/tax?from=${f}&to=${t}&jurisdiction=${j}`);
      setData(res ?? null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    fetchData(from, to, jurisdiction);
  }, [fetchData, from, to, jurisdiction]);

  const run = () => { loadedRef.current = false; fetchData(from, to, jurisdiction); };

  const switchJurisdiction = (j: string) => {
    setJurisdiction(j);
    loadedRef.current = false;
    fetchData(from, to, j);
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Receipt className="h-6 w-6 text-muted-foreground" />
        <div>
          <h1 className="text-xl font-semibold">Tax Report</h1>
          <p className="text-sm text-muted-foreground">Output / input tax reconciliation</p>
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="py-3 flex flex-wrap gap-3 items-center">
          <div className="flex gap-1">
            {JURISDICTIONS.map(j => (
              <button
                key={j.id}
                onClick={() => switchJurisdiction(j.id)}
                className={`px-3 py-1 text-xs rounded border transition-colors ${
                  jurisdiction === j.id
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border hover:bg-muted'
                }`}
              >
                {j.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2 items-center ml-auto">
            <label className="text-xs text-muted-foreground">From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="text-sm border rounded px-2 py-1" />
            <label className="text-xs text-muted-foreground">To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} className="text-sm border rounded px-2 py-1" />
            <Button size="sm" onClick={run} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Run
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Loading tax report…</div>
      ) : !data ? (
        <div className="text-center py-12 text-muted-foreground text-sm">No data available for selected period.</div>
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Card>
              <CardContent className="py-4">
                <p className="text-xs text-muted-foreground mb-1">Output Tax Collected</p>
                <p className="text-2xl font-bold text-orange-600">{fmt(data.summary.totalOutputTax)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">From customers ({(data.taxRate * 100).toFixed(0)}%)</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4">
                <p className="text-xs text-muted-foreground mb-1">Input Tax Claimable</p>
                <p className="text-2xl font-bold text-blue-600">{fmt(data.summary.totalInputTax)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Paid to suppliers</p>
              </CardContent>
            </Card>
            <Card className={data.summary.isRefund ? 'border-green-200' : 'border-orange-200'}>
              <CardContent className="py-4">
                <p className="text-xs text-muted-foreground mb-1">
                  Net Tax {data.summary.isRefund ? 'Refundable' : 'Payable'}
                </p>
                <p className={`text-2xl font-bold ${data.summary.isRefund ? 'text-green-600' : 'text-red-600'}`}>
                  {data.summary.isRefund && '('}{fmt(Math.abs(data.summary.netTaxPayable))}{data.summary.isRefund && ')'}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">Output − Input</p>
              </CardContent>
            </Card>
          </div>

          {/* SG GST F5 Indicative Boxes */}
          {data.gstBoxes && (
            <Card>
              <CardContent className="py-4">
                <p className="text-sm font-semibold mb-3">Singapore GST F5 — Indicative Values</p>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
                  {([
                    { box: 'Box 1', label: 'Std-rated supplies', val: data.gstBoxes.box1 },
                    { box: 'Box 5', label: 'Taxable purchases',  val: data.gstBoxes.box5 },
                    { box: 'Box 6', label: 'Output tax due',     val: data.gstBoxes.box6 },
                    { box: 'Box 7', label: 'Input tax claimable',val: data.gstBoxes.box7 },
                    { box: 'Box 9', label: 'Net GST payable',    val: data.gstBoxes.box9 },
                  ] as const).map(b => (
                    <div key={b.box} className="bg-muted/50 rounded p-2">
                      <p className="text-xs font-semibold text-primary">{b.box}</p>
                      <p className="text-[10px] text-muted-foreground mb-1">{b.label}</p>
                      <p className="text-sm font-bold">{fmt(b.val)}</p>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-2 italic">* Indicative only. Verify with your tax advisor before IRAS submission.</p>
              </CardContent>
            </Card>
          )}

          {/* Output Tax Table */}
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="h-4 w-4 text-orange-500" />
                <p className="text-sm font-semibold">Output Tax Breakdown</p>
                <Badge variant="outline" className="text-xs ml-auto">{data.outputLines.length} types</Badge>
              </div>
              {data.outputLines.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No output tax entries in this period.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="pr-3">Entry Type</TableHead>
                        <TableHead className="text-right pr-3">Count</TableHead>
                        <TableHead className="text-right pr-3">Gross Amount</TableHead>
                        <TableHead className="text-right pr-3">Taxable Amount</TableHead>
                        <TableHead className="text-right">Tax Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.outputLines.map(l => (
                        <TableRow key={l.code} className="border-b last:border-0 hover:bg-muted/30">
                          <TableCell className="pr-3 font-mono">{l.code}</TableCell>
                          <TableCell className="text-right pr-3 tabular-nums">{l.count}</TableCell>
                          <TableCell className="text-right pr-3 tabular-nums">{fmt(l.grossAmount)}</TableCell>
                          <TableCell className="text-right pr-3 tabular-nums">{fmt(l.taxableAmount)}</TableCell>
                          <TableCell className="text-right tabular-nums font-semibold text-orange-700">{fmt(l.taxAmount)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-orange-50 font-semibold border-t-2">
                        <TableCell className="pr-3" colSpan={4}>Total Output Tax</TableCell>
                        <TableCell className="text-right tabular-nums text-orange-700">{fmt(data.summary.totalOutputTax)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Input Tax Table */}
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingDown className="h-4 w-4 text-blue-500" />
                <p className="text-sm font-semibold">Input Tax Breakdown (Claimable)</p>
                <Badge variant="outline" className="text-xs ml-auto">{data.inputLines.length} types</Badge>
              </div>
              {data.inputLines.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No input tax entries in this period.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="pr-3">Entry Type</TableHead>
                        <TableHead className="text-right pr-3">Count</TableHead>
                        <TableHead className="text-right pr-3">Gross Amount</TableHead>
                        <TableHead className="text-right pr-3">Taxable Amount</TableHead>
                        <TableHead className="text-right">Tax Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.inputLines.map(l => (
                        <TableRow key={l.code} className="border-b last:border-0 hover:bg-muted/30">
                          <TableCell className="pr-3 font-mono">{l.code}</TableCell>
                          <TableCell className="text-right pr-3 tabular-nums">{l.count}</TableCell>
                          <TableCell className="text-right pr-3 tabular-nums">{fmt(l.grossAmount)}</TableCell>
                          <TableCell className="text-right pr-3 tabular-nums">{fmt(l.taxableAmount)}</TableCell>
                          <TableCell className="text-right tabular-nums font-semibold text-blue-700">{fmt(l.taxAmount)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-blue-50 font-semibold border-t-2">
                        <TableCell className="pr-3" colSpan={4}>Total Input Tax (Claimable)</TableCell>
                        <TableCell className="text-right tabular-nums text-blue-700">{fmt(data.summary.totalInputTax)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
